import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin,
  ChevronUp,
  Phone,
  MessageSquare,
  Play,
  Award,
  Users,
  ShieldCheck,
  Clock,
  CheckCircle2,
  Mail,
  User,
} from "lucide-react";
import PageLayout from "@/components/PageLayout";
import Footer from "@/components/Footer";
import FloatingContactBar from "@/components/FloatingContactBar";

const videoGallery = [
  {
    id: 1,
    title: "Dubai Air Show",
    location: "DWC, Dubai",
    desc: "A showcase of cutting-edge aviation structures designed to withstand high-traffic demands. Our premium chalets provided a sophisticated environment for global aerospace leaders.",
    video: "https://res.cloudinary.com/dizdlq9fo/video/upload/v1774933136/Airshow_2025_uzzqko.mp4",
  },
  {
    id: 2,
    title: "ADIPEC",
    location: "ADNEC, Abu Dhabi",
    desc: "Supporting the world’s most influential energy transition event with robust industrial tent solutions and climate-controlled pavilions engineered for heavy-duty requirements.",
    video: "https://res.cloudinary.com/dizdlq9fo/video/upload/v1774933173/Adipec_2025_diybt8.mp4",
  },
  {
    id: 3,
    title: "Gulf Food",
    location: "DWTC, Dubai",
    desc: "Customized hospitality and exhibition structures designed for the massive scale of the world's largest food and beverage show, featuring hygienic environments and reinforced flooring.",
    video: "https://res.cloudinary.com/dizdlq9fo/video/upload/v1774933205/Gulf_Food_2026_nfrsxf.mp4",
  },
  {
    id: 4,
    title: "GITEX Global",
    location: "Dubai Harbor / DWTC",
    desc: "Modern, futuristic structures built to house the latest in global technology and AI innovations. High-clearance tents provided the perfect canvas for massive LED displays.",
    video: "https://res.cloudinary.com/dizdlq9fo/video/upload/v1774933202/Gitex_2025_gi0fxh.mp4",
  },
  {
    id: 5,
    title: "DriftX",
    location: "Yas Marina, Abu Dhabi",
    desc: "An outdoor installation focused on the future of autonomous mobility. We delivered expansive viewing areas and technical paddocks designed for coastal weather.",
    video: "https://res.cloudinary.com/dizdlq9fo/video/upload/v1774933182/Drift_X_ixz5iz.mp4",
  },
  {
    id: 6,
    title: "Netflix - Stranger Things Experience",
    location: "The Deck, Dubai",
    desc: "A fully immersive, themed structural build created in partnership with Netflix to bring a fan-favorite world to life, requiring intricate soundproofing and light-control.",
    video: "https://res.cloudinary.com/dizdlq9fo/video/upload/v1774933196/Netflix_Stranger_Things_mnwirc.mp4",
  },
  {
    id: 7,
    title: "Dubai Health Expo",
    location: "Dubai World Trade Centre",
    desc: "Sterile, well-ventilated exhibition spaces tailored for the healthcare community. Safety and modular accessibility were the primary focus of this massive deployment.",
    video: "https://res.cloudinary.com/dizdlq9fo/video/upload/v1774933172/Dubai_Health_Expo_ttzvon.mp4",
  },
  {
    id: 8,
    title: "World Taekwondo Championship",
    location: "Fujairah, UAE",
    desc: "A dynamic sports arena build that offered high-tension fabric roofing for maximum internal clearance, spectator seating, and athlete preparation areas.",
    video: "https://res.cloudinary.com/dizdlq9fo/video/upload/v1774933137/Taekwando_2026_w2ec7t.mp4",
  },
  {
    id: 9,
    title: "Amaal Sales Centre",
    location: "Meydan, Dubai",
    desc: "A premium, glass-walled temporary real estate sales office that reflects luxury. It demonstrates how temporary structures can rival permanent buildings in quality.",
    video: "https://res.cloudinary.com/dizdlq9fo/video/upload/v1774933188/Amaal_qgmzu2.mp4",
  },
  {
    id: 10,
    title: "Al Marwan Sales Centre",
    location: "Sharjah, UAE",
    desc: "A bespoke commercial marquee designed as a high-functioning sales hub. This structure utilized insulated panels and premium glass doors for energy efficiency.",
    video: "https://res.cloudinary.com/dizdlq9fo/video/upload/v1774933196/Al_Marwan_mpj6dk.mp4",
  },
  {
    id: 11,
    title: "Medlab",
    location: "DWTC, Dubai",
    desc: "Precision-engineered exhibition halls that support the rigorous standards of laboratory medicine, including complex electrical layouts for sensitive machinery.",
    video: "https://res.cloudinary.com/dizdlq9fo/video/upload/v1774933193/Medlab_2025_ngduyx.mp4",
  },
  {
    id: 12,
    title: "FIE Fujairah",
    location: "Fujairah, UAE",
    desc: "High-durability tent solutions for the Fujairah industrial sector. These structures were designed for rapid assembly across a variety of terrain types.",
    video: "https://res.cloudinary.com/dizdlq9fo/video/upload/v1774933192/Fie_Fujairah_uicwnr.mp4",
  },
];

const trustItems = [
  { icon: Award, title: "30+", sub: "Years Experience" },
  { icon: Users, title: "8000+", sub: "Projects Delivered" },
  { icon: ShieldCheck, title: "UAE-Wide", sub: "Fast Installation" },
  { icon: Clock, title: "Expert", sub: "Team & Support" },
];

const ScrollVideo = ({ src, title }: { src: string; title: string }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            videoRef.current?.play().catch(() => {});
          } else {
            videoRef.current?.pause();
          }
        });
      },
      { threshold: 0.35 }
    );

    if (videoRef.current) observer.observe(videoRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <video style={{ objectFit: 'cover', width: '100%', height: '100%' }}
      ref={videoRef}
      muted
      loop
      playsInline
      preload="metadata"
      aria-label={title}
      className="absolute inset-0 w-full h-full object-cover"
    >
      <source src={src} type="video/mp4" />
    </video>
  );
};

const MiniQuoteForm = ({ quoteRef }: { quoteRef: React.RefObject<HTMLDivElement> }) => {
  const navigate = useNavigate();
  const [geo, setGeo] = useState({ code: "+971", country: "AE" });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    requirements: "",
  });

  useEffect(() => {
    const controller = new AbortController();

    const geoTimeout = setTimeout(() => {
      fetch("https://ipapi.co/json/", { signal: controller.signal })
        .then((res) => res.json())
        .then((data) => {
          if (data.country_calling_code) {
            const formattedCode = data.country_calling_code.startsWith("+")
              ? data.country_calling_code
              : `+${data.country_calling_code}`;

            setGeo({
              code: formattedCode,
              country: data.country_code || "AE",
            });
          }
        })
        .catch(() => {});
    }, 3000);

    return () => {
      controller.abort();
      clearTimeout(geoTimeout);
    };
  }, []);

  const handleMiniSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    navigate("/thankyou");

    const SCRIPT_URL =
      "https://script.google.com/macros/s/AKfycby5HKEZNMObZRaeTidKlOMCLR_2YpgZuP2jOscP-OHWz2pi8PT0j8giTglCwm0VO5_J/exec";

    fetch(SCRIPT_URL, {
      method: "POST",
      mode: "no-cors",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...formData,
        phone: `${geo.code} ${formData.phone}`,
        source: "Videos Page",
      }),
    }).catch(() => {});
  };

  return (
    <div
      ref={quoteRef}
      className="relative w-full rounded-[2rem] bg-[#0c0c0c] shadow-none md:shadow-[0_30px_80px_rgba(0,0,0,0.55)] border-0 md:border md:border-purple-500/20 p-6 md:p-8 overflow-hidden scroll-mt-24"
    >
      <div className="hidden md:block absolute top-0 right-0 w-72 h-72 bg-purple-600/10 blur-[100px] rounded-full pointer-events-none" />

      <div className="relative z-10">
        <div className="border-b border-white/10 pb-5 mb-5">
          <h2 className="text-xl font-black text-white uppercase tracking-tight">
            Quick Project Query
          </h2>
          <p className="text-[9px] font-black text-[#bd6aff] uppercase tracking-[0.24em] mt-2">
            Event, Pavilion & Temporary Structure Enquiry
          </p>
        </div>

        <form className="space-y-4" onSubmit={handleMiniSubmit}>
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              type="text"
              placeholder="Full Name"
              required
              className="w-full bg-[#0d0d0d] border border-zinc-800 rounded-xl pl-11 pr-4 py-3.5 text-zinc-200 text-[11px] focus:outline-none focus:border-purple-600 transition-colors placeholder:text-zinc-500"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>

          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              type="email"
              placeholder="Email Address"
              required
              className="w-full bg-[#0d0d0d] border border-zinc-800 rounded-xl pl-11 pr-4 py-3.5 text-zinc-200 text-[11px] focus:outline-none focus:border-purple-600 transition-colors placeholder:text-zinc-500"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>

          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-purple-400 text-[10px] font-black border-r border-zinc-800 pr-3">
              {geo.code}
            </div>
            <input
              type="tel"
              required
              placeholder="Mobile Number"
              className="w-full bg-[#0d0d0d] border border-zinc-800 rounded-xl pl-16 pr-4 py-3.5 text-zinc-200 text-[11px] focus:outline-none focus:border-purple-600 transition-colors placeholder:text-zinc-500"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
          </div>

          <textarea
            placeholder="Tell us about your project, event, location, size or deadline..."
            className="w-full h-24 md:h-28 bg-[#0d0d0d] border border-zinc-800 rounded-xl px-4 py-3.5 text-zinc-200 text-[11px] focus:outline-none resize-none focus:border-purple-600 transition-colors placeholder:text-zinc-500"
            value={formData.requirements}
            onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
          />

          <button
            type="submit"
            className="w-full py-4 bg-gradient-to-r from-[#a43cff] to-purple-700 text-white font-black rounded-xl uppercase tracking-widest text-[11px] shadow-[0_10px_30px_rgba(139,49,255,0.24)] active:scale-95 transition-all hover:brightness-110"
          >
            Send Request
          </button>
        </form>
      </div>
    </div>
  );
};

const Videos = () => {
  const quoteRef = useRef<HTMLDivElement>(null);
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    document.title = "Project Videos & Tent Setup Showcases | BA Tents UAE";

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "Explore BA Tents project videos, event tent setups, exhibition pavilions, sports structures, and temporary tent installations across UAE and GCC."
      );
    }

    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    window.scrollTo(0, 0);

    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 500);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToQuote = useCallback(() => {
    quoteRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <PageLayout>
      <div className="min-h-screen bg-black text-white overflow-hidden w-screen max-w-none relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] -mt-28 md:-mt-12">
        <section className="relative pt-28 md:pt-32 pb-10 md:pb-16 bg-black">
          <div className="absolute inset-0 bg-black pointer-events-none" />
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/40 to-black pointer-events-none" />

          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid lg:grid-cols-[1.35fr_0.85fr] gap-8 lg:gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/20 bg-white/10 mb-5">
                  <Play className="w-3.5 h-3.5 text-[#bd6aff] fill-[#bd6aff]" />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-white">
                    BA Tents Project Portfolio
                  </span>
                </div>

                <h1 className="text-[36px] md:text-[64px] font-black text-white leading-[1.05] mb-5 tracking-tight uppercase">
                  Project Highlights <br className="hidden md:block" />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#bd6aff] to-purple-400">
                    & Video Showcases
                  </span>
                </h1>

                <p className="text-zinc-300 text-sm md:text-base leading-relaxed max-w-2xl mb-7">
                  Explore BA Tents event structures, pavilions, sports venues, sales centres, and temporary installations delivered across UAE and GCC.
                </p>

                <div className="flex flex-row items-center gap-1.5 md:gap-3 max-w-xl mb-8">
                  <button
                    onClick={scrollToQuote}
                    className="flex-1 py-4 bg-gradient-to-r from-[#8b31ff] to-[#bd6aff] rounded-xl text-white font-black text-[10px] md:text-xs tracking-wider flex items-center justify-center gap-1.5 uppercase shadow-none md:shadow-[0_0_20px_rgba(139,49,255,0.3)] border-0 md:border active:scale-95 transition-transform"
                  >
                    <Phone className="w-3.5 h-3.5" />
                    <span>Get Quote</span>
                  </button>

                  <a
                    href="tel:+971588243660"
                    className="flex-1 py-4 bg-white rounded-xl text-black font-black text-[10px] md:text-xs flex items-center justify-center gap-1.5 uppercase border-0 shadow-none active:scale-95 transition-transform"
                  >
                    <Phone className="w-3.5 h-3.5" />
                    <span>Call Now</span>
                  </a>

                  <a
                    href="https://wa.me/971588243660"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 py-4 bg-emerald-500 rounded-xl text-white font-black text-[10px] md:text-xs flex items-center justify-center gap-1.5 uppercase border-0 shadow-none active:scale-95 transition-transform"
                  >
                    <MessageSquare className="w-3.5 h-3.5" />
                    <span>WhatsApp</span>
                  </a>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl pt-5 border-t border-white/10">
                  {trustItems.map((item, i) => (
                    <div key={i} className="flex items-center gap-3 text-left">
                      <div className="p-2 bg-purple-500/10 rounded-lg shrink-0">
                        <item.icon className="w-5 h-5 text-[#bd6aff]" />
                      </div>
                      <div>
                        <p className="text-white font-bold text-sm leading-none whitespace-nowrap">{item.title}</p>
                        <p className="text-zinc-300 text-[9px] uppercase tracking-wider font-bold mt-1.5">{item.sub}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <MiniQuoteForm quoteRef={quoteRef} />
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-20">
          <div className="max-w-4xl mx-auto text-center mb-10 md:mb-16">
            <p className="text-[#bd6aff] text-[10px] font-black uppercase tracking-[0.32em] mb-3">
              Latest Installations
            </p>
            <h2 className="text-3xl md:text-6xl font-black uppercase tracking-tight text-white leading-[0.95] mb-5">
              Signature Project <span className="text-[#bd6aff]">Showcases</span>
            </h2>
            <p className="text-zinc-400 text-sm md:text-base leading-relaxed max-w-3xl mx-auto">
              Watch full-size project videos from BA Tents installations across exhibitions, sports venues, aviation events, pavilions, and corporate experiences.
            </p>
          </div>

          <div className="flex flex-col gap-10 md:gap-16">
            {videoGallery.map((video) => (
              <motion.article
                key={video.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true, margin: "-100px" }}
                className="group relative overflow-hidden rounded-[2rem] md:rounded-[3rem] bg-[#080808] shadow-[0_30px_90px_rgba(0,0,0,0.65)]"
              >
                <div className="relative p-3 md:p-5">
                  <div className="relative w-full aspect-video overflow-hidden rounded-[1.5rem] md:rounded-[2.5rem] bg-black">
                    <ScrollVideo src={video.video} title={video.title} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent pointer-events-none" />
                  </div>
                </div>

                <div className="px-6 md:px-10 lg:px-12 pb-7 md:pb-10 pt-3 md:pt-5">
                  <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-6 md:gap-10 items-start">
                    <div>
                      <div className="inline-flex items-center gap-2 text-[#bd6aff] text-[10px] font-black uppercase tracking-[0.24em] px-3 py-2 rounded-full bg-white/[0.04] mb-4">
                        <MapPin className="w-3.5 h-3.5" />
                        {video.location}
                      </div>

                      <h2 className="text-3xl md:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-[0.95] text-white group-hover:text-[#bd6aff] transition-colors duration-300">
                        {video.title}
                      </h2>
                    </div>

                    <div className="lg:pt-8">
                      <p className="text-zinc-400 text-sm md:text-base leading-relaxed max-w-3xl">
                        {video.desc}
                      </p>

                      <div className="mt-6 flex flex-wrap gap-3 items-center">
                        <button
                          onClick={scrollToQuote}
                          className="px-6 py-4 rounded-2xl bg-white text-black hover:bg-[#bd6aff] hover:text-white transition-all duration-300 text-[10px] md:text-xs font-black uppercase tracking-[0.22em]"
                        >
                          Discuss Similar Project
                        </button>

                        <a
                          href="https://wa.me/971588243660"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-6 py-4 rounded-2xl bg-white/5 text-white hover:bg-white/10 hover:text-[#bd6aff] transition-all duration-300 text-[10px] md:text-xs font-black uppercase tracking-[0.22em]"
                        >
                          WhatsApp Team
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </section>
      </div>

      <AnimatePresence>
        {showBackToTop && (
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            onClick={scrollToTop}
            aria-label="Back to top"
            className="hidden md:flex fixed bottom-24 right-8 z-[100] group flex-col items-center gap-3"
          >
            <div className="relative w-[2px] h-16 overflow-hidden bg-white/10 rounded-full">
              <motion.div
                animate={{ y: [64, -64], opacity: [0, 1, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                className="absolute inset-0 w-full h-1/2 bg-gradient-to-t from-transparent via-white to-transparent"
              />
            </div>

            <div className="w-12 h-12 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm flex items-center justify-center group-hover:bg-white group-hover:scale-110 transition-all duration-500 shadow-2xl">
              <ChevronUp className="w-6 h-6 text-white group-hover:text-black transition-colors duration-300 stroke-[3px]" />
            </div>
          </motion.button>
        )}
      </AnimatePresence>

      <FloatingContactBar onQuoteClick={scrollToQuote} />
      <Footer />
    </PageLayout>
  );
};

export default Videos;
